import { createReducer } from '@reduxjs/toolkit';
import { collectTransforms } from 'Internals/pipes';
import { TransformMap } from 'Types/common.types';
import type { Pipe, PipeResponse } from 'Types/pipes.types';

import { disconnected } from '../thunks/subscriptions.thunks';
import {
  pipeAdded,
  pipeUpdated,
  pipeLoaded,
  pipesLoadAllFinished,
  pipesRemoved,
} from '../thunks/pipes.thunks';
import { systemRemoved } from '../thunks/systems.thunks';

type TransformsState = TransformMap;

function setTransform(pipe: Pipe | PipeResponse, state: TransformsState) {
  const transforms = collectTransforms(pipe);
  if (transforms.length < 1) {
    return;
  }
  for (const id of transforms) {
    if (state[id]) {
      state[id][pipe._id] = true;
    } else {
      state[id] = { [pipe._id]: true };
    }
  }
}

const initialState: TransformsState = {};

/*
Note: We are using the builder callback for creating a typesafe reducer.
See https://redux-toolkit.js.org/usage/usage-with-typescript#building-type-safe-reducer-argument-objects
*/
export const transformsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(disconnected, () => initialState)
    .addCase(pipeAdded, (state, action) => {
      setTransform(action.payload, state);
    })
    .addCase(pipeUpdated, (state, action) => {
      setTransform(action.payload.pipe, state);
    })
    .addCase(pipeLoaded, (state, action) => {
      setTransform(action.payload.pipe, state);
    })
    .addCase(pipesLoadAllFinished, (state, action) => {
      const newState = {};
      for (let i = 0; i < action.payload.length; i++) {
        setTransform(action.payload[i], newState);
      }
      return newState;
    })
    .addCase(pipesRemoved, (state, action) => {
      action.payload.forEach((p) => {
        if (p.sourceSystemId && state[p.sourceSystemId]) {
          delete state[p.sourceSystemId][p.id];
        }
      });
    })
    .addCase(systemRemoved, (state, action) => {
      action.payload.forEach((systemId) => {
        if (state[systemId]) {
          delete state[systemId];
        }
      });
    })
);
